/** @jsxImportSource @emotion/react */
import {
  useCallback,
  useReducer,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react'
import { App } from 'antd'
import { answersReducer } from 'src/utils/dynamicFormReducer'
import { prefillDynamicFormAnswers } from 'src/utils/prefillDynamicFormAnswers'
import { submitDynamicRegistration } from '../Schedule/RegistrationDetailsModal/utils/submitDynamicRegistration'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { mapInvalidFields } from 'src/utils/invalidFieldMapper'
import DynamicForm from 'src/components/DynamicForm'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { UsersMeRegistrationResponse } from '@sportninja/common/types/usersMeRegistration'

interface RegistrationDetailsDynamicStandaloneProps {
  scheduleId: string
  registrationUid: string
  viewMode?: boolean
  refetchParent?: () => void
  onSubmitReady?: (submitFn: () => void) => void
  registrationDetails: UsersMeRegistrationResponse | null
  loading: boolean
  error: Error | null
  refetch: () => void
}

export const getSelectedForm = (
  registration: any,
  formType: string
): any | null => {
  if (!registration) {
    return null
  }
  const form =
    formType === 'team' ? registration.team_form : registration.player_form

  return form || null
}

/**
 * 🔄 RegistrationDetailsDynamicStandalone: Standalone dynamic form component for registration details
 *
 * This component handles all dynamic form logic for the registration details page.
 * It uses useUsersMeRegistration to fetch registration data directly and useRegistrationData
 * to fetch form definitions, managing form state independently.
 *
 * Key differences from RegistrationDetailsDynamicForm:
 * - Uses useUsersMeRegistration instead of useRegistrationDetailsModal context
 * - Designed for standalone page usage, not modal usage
 * - Simplified props interface focused on page-level needs
 *
 * SUBMIT FUNCTION EXPOSURE:
 * - Uses useRef (submitFunctionRef) to store the actual form submit function
 * - Exposes a stable submit function to parent via onSubmitReady callback
 * - Parent component stores this function in its own useRef to avoid re-renders
 * - This approach ensures the submit function is always available and prevents
 *   timing issues that can occur with useState-based approaches
 */
const RegistrationDetailsDynamicStandalone = ({
  scheduleId,
  registrationUid,
  viewMode = false,
  refetchParent,
  onSubmitReady,
  registrationDetails,
  loading,
  error,
  refetch,
}: RegistrationDetailsDynamicStandaloneProps) => {
  console.log(
    '🔄 RegistrationDetailsDynamicStandalone: Initializing component',
    {
      scheduleId,
      registrationUid,
      viewMode,
    }
  )

  const { message } = App.useApp()
  const [answers, dispatchAnswer] = useReducer(answersReducer, {})
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})
  const submitFunctionRef = useRef<(() => void) | null>(null)

  // Determine registration context from the fetched data
  const isTeamRegistration = useMemo(() => {
    return registrationDetails?.registration_type === 'Team'
  }, [registrationDetails?.registration_type])

  const isApprovedPlayer = useMemo(() => {
    return registrationDetails?.player !== null
  }, [registrationDetails?.player])

  console.log('🔄 RegistrationDetailsDynamicStandalone: Registration context', {
    isTeamRegistration,
    isApprovedPlayer,
    registrationType: registrationDetails?.registration_type,
    hasPlayer: !!registrationDetails?.player,
    hasTeam: !!registrationDetails?.team,
  })

  // Get selected form based on registration type (team vs player)
  const selectedForm = useMemo(() => {
    console.log('registrationDetails', registrationDetails)
    if (!registrationDetails) {
      return null
    }
    const formType = isTeamRegistration ? 'team' : 'player'
    return getSelectedForm(registrationDetails, formType)
  }, [registrationDetails, isTeamRegistration])

  // Only disable fields if viewMode is true (read-only view)
  const disabledFields = useMemo(() => {
    if (!viewMode || !selectedForm) {
      return new Set<string>()
    }
    return new Set(
      selectedForm.groups.flatMap((group) => group.items.map((item) => item.id))
    )
  }, [selectedForm, viewMode])

  console.log(
    '🔄 RegistrationDetailsDynamicStandalone: Disabled fields computed',
    {
      disabledFieldsCount: disabledFields.size,
      hasSelectedForm: !!selectedForm,
      formGroups: selectedForm?.groups.length,
    }
  )

  // Prefill answers from saved registration data when both form and data are available
  useEffect(() => {
    if (selectedForm && registrationDetails?.form) {
      console.log(
        '🔄 RegistrationDetailsDynamicStandalone: Prefilling form with saved data',
        {
          selectedFormId: selectedForm.id,
          registrationFormSections: Object.keys(registrationDetails.form),
          registrationDetailsUid: registrationDetails.uid,
        }
      )

      // Convert FormField[] to the expected format, filtering out null values
      const convertedForm: {
        [sectionKey: string]: Array<{ id: string; name: string; value: string }>
      } = {}
      Object.entries(registrationDetails.form).forEach(
        ([sectionKey, fields]) => {
          convertedForm[sectionKey] = fields
            .filter((field) => field.value !== null)
            .map((field) => ({
              id: field.id,
              name: field.name,
              value: field.value as string, // Safe cast since we filtered out nulls
            }))
        }
      )

      const prefilledAnswers = prefillDynamicFormAnswers(
        selectedForm,
        convertedForm
      )

      if (Object.keys(prefilledAnswers).length > 0) {
        dispatchAnswer({ type: 'INIT_STATE', payload: prefilledAnswers })
        console.log(
          '🔄 RegistrationDetailsDynamicStandalone: Form prefilled successfully',
          {
            prefilledFieldCount: Object.keys(prefilledAnswers).length,
            prefilledFields: Object.keys(prefilledAnswers),
          }
        )
      } else {
        console.log(
          '🔄 RegistrationDetailsDynamicStandalone: No matching fields found for prefill'
        )
      }
    } else {
      console.log(
        '🔄 RegistrationDetailsDynamicStandalone: Prefill conditions not met',
        {
          hasSelectedForm: !!selectedForm,
          hasRegistrationForm: !!registrationDetails?.form,
          registrationDetailsUid: registrationDetails?.uid,
        }
      )
    }
  }, [selectedForm, registrationDetails?.form, registrationDetails?.uid])

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      if (!registrationDetails?.uid || !selectedForm) {
        console.error(
          '🔄 RegistrationDetailsDynamicStandalone: Missing required data for submission'
        )
        return
      }

      // Clear previous errors
      setFieldErrors([])
      setCustomError({})

      console.log('🔄 RegistrationDetailsDynamicStandalone: Form submitted', {
        registrationDetails: registrationDetails?.uid,
      })

      await submitDynamicRegistration({
        scheduleId,
        registrationUid: registrationDetails.uid,
        isTeamRegistration,
        isApprovedPlayer,
        answers,
        formDefinition: selectedForm,
        playerContext: registrationDetails?.player?.id
          ? { id: registrationDetails.player.id }
          : undefined,
        teamContext:
          registrationDetails?.team?.uid &&
          registrationDetails?.team?.team_player_roster_id
            ? {
                uid: registrationDetails.team.uid,
                rosterId: registrationDetails.team.team_player_roster_id,
              }
            : undefined,
      })

      message.open({
        type: 'success',
        content: 'Registration Updated Successfully',
      })

      // Remove registration_id from URL after successful submission
      const cleanupUrl = new URL(window.location.href)
      cleanupUrl.searchParams.delete('registration_id')
      window.history.pushState({}, '', cleanupUrl.toString())

      // Refetch registration data and parent component
      await refetch()
      if (refetchParent) {
        refetchParent()
      }
    } catch (e: any) {
      console.error(
        '🔄 RegistrationDetailsDynamicStandalone: Submission error',
        e
      )

      // Handle validation errors with field-specific feedback
      if (e?.invalid_fields) {
        const { fieldErrors, customError } = mapInvalidFields(e.invalid_fields)
        setFieldErrors(fieldErrors)
        setCustomError(customError)
        return
      }

      // Handle general errors
      const errorMessage = getErrorMessage(e)
      message.open({
        type: 'error',
        content: errorMessage,
      })
    }
  }, [
    registrationDetails,
    scheduleId,
    isTeamRegistration,
    isApprovedPlayer,
    message,
    refetch,
    refetchParent,
    selectedForm,
    answers,
  ])

  // Expose the submit function to the parent component using useRef
  // This ensures the function is always available and avoids re-render issues
  useEffect(() => {
    if (onSubmitReady) {
      console.log(
        '🔄 RegistrationDetailsDynamicStandalone: Setting up submit function for parent'
      )
      onSubmitReady(() => {
        console.log(
          '🔄 RegistrationDetailsDynamicStandalone: External submit triggered via useRef'
        )
        if (submitFunctionRef.current) {
          submitFunctionRef.current()
        } else {
          console.warn(
            '🔄 RegistrationDetailsDynamicStandalone: No submit function available in ref'
          )
        }
      })
    }
  }, [onSubmitReady])

  // Show loading state
  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
        `}
      >
        Loading form...
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
          color: ${colors.FIERY_RED};
        `}
      >
        Error loading form: {error?.message || 'Unknown error'}
      </div>
    )
  }

  // Show empty state if no form
  if (!selectedForm) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
          color: ${colors.SOFT_STEEL};
        `}
      >
        No form available
      </div>
    )
  }

  return (
    <DynamicForm
      groups={selectedForm.groups}
      sportId={
        registrationDetails?.schedule?.sport_id
          ? registrationDetails.schedule.sport_id
          : undefined
      }
      answers={answers}
      dispatchAnswer={dispatchAnswer}
      loading={loading}
      onSubmit={handleSubmit}
      nextButtonText='Save'
      hideButtons={true}
      submitRef={submitFunctionRef}
      fieldErrors={fieldErrors}
      customError={customError}
      disabledFields={disabledFields as Set<string>}
    />
  )
}

export default RegistrationDetailsDynamicStandalone
